import { supabase } from './supabase';
import type { Parcours, ParcoursStep } from '../types/parcours';

type EventType = 'view_parcours_step' | 'start_parcours' | 'complete_step' | 'complete_parcours';

interface LogParams {
  eventType: EventType;
  parcours: Parcours;
  step?: ParcoursStep;
}

function dedupeKey(eventType: string, userId: string, parcoursId: string, stepId?: string): string {
  return `candidate_activity_${eventType}_${userId}_${parcoursId}_${stepId ?? ''}`;
}

function isRecentlyLogged(key: string, windowMs = 30 * 60 * 1000): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    return Date.now() - parseInt(raw, 10) < windowMs;
  } catch {
    return false;
  }
}

function markLogged(key: string): void {
  try {
    localStorage.setItem(key, String(Date.now()));
  } catch {
    // localStorage unavailable — skip
  }
}

export async function logCandidateActivity({ eventType, parcours, step }: LogParams): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const user = session.user;
    const userId = user.id;
    const userEmail = user.email ?? null;
    const provider =
      (user.app_metadata?.provider as string | undefined) ??
      (user.identities?.[0]?.provider ?? null);

    const stepId = step?.step_id ?? null;
    const key = dedupeKey(eventType, userId, parcours.id, stepId ?? undefined);

    if (isRecentlyLogged(key)) return;

    const { error } = await supabase.from('candidate_activity_logs').insert({
      user_id: userId,
      user_email: userEmail,
      auth_provider: provider,
      event_type: eventType,
      parcours_id: parcours.id,
      parcours_slug: parcours.slug,
      parcours_company_name: parcours.company_name,
      step_id: stepId,
      step_title: step?.title ?? null,
      page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });

    if (error) {
      console.warn('[candidateActivity] insert error:', error.message);
      return;
    }

    markLogged(key);
  } catch (err) {
    console.warn('[candidateActivity] unexpected error:', err);
  }
}
